import { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { apiFetch } from "../utility/apiFetch.js";
import Layout from "../Layouts/Layout.jsx";
import Spinner from "../components/Spinner.jsx";
import SettingsThresholds from "../components/SettingsThresholds.jsx";
import useSessionStore from "../stores/sessionStore.js";
import usePlan from "../hooks/usePlan.js";
import {
  useTeamRosterQuery,
  useSendInvite,
  useCancelInvite,
  useRemoveMember,
  useUpdateTeamName,
} from "../hooks/useTeamQuery.js";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const TIMEZONES = Intl.supportedValuesOf("timeZone");

function TimezoneSettings() {
  const user = useSessionStore((s) => s.user);
  const setUser = useSessionStore((s) => s.setUser);
  const [selected, setSelected] = useState(user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await apiFetch("profile/timezone", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ timezone: selected }),
      });
      setUser({ ...user, timezone: selected });
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4">
      <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">Timezone</h2>
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={selected}
          onChange={(e) => { setSelected(e.target.value); setSaved(false); }}
          className="border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white dark:bg-gray-700 dark:text-gray-100"
        >
          {TIMEZONES.map((tz) => (
            <option key={tz} value={tz}>{tz}</option>
          ))}
        </select>
        <button
          onClick={handleSave}
          disabled={saving}
          className="text-sm bg-sky-600 dark:bg-sky-700 text-white rounded-lg px-4 py-2 hover:bg-sky-700 dark:hover:bg-sky-600 transition-colors disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {saved && <span className="text-sm text-green-600 dark:text-green-400">Saved</span>}
        {error && <span className="text-sm text-red-600 dark:text-red-400">{error}</span>}
      </div>
    </div>
  );
}

const PLAN_MAX_MEMBERS = { hobby: 1, pro: 3, enterprise: "∞" };

function TeamSettings({ plan }) {
  const currentUser = useSessionStore((s) => s.user);
  const setUser = useSessionStore((s) => s.setUser);
  const currentUID = currentUser?.profile?.id ?? currentUser?.id;

  const { data, isLoading, error } = useTeamRosterQuery();
  const sendInvite = useSendInvite();
  const cancelInvite = useCancelInvite();
  const removeMember = useRemoveMember();
  const updateTeamName = useUpdateTeamName();

  const [teamName, setTeamName] = useState(currentUser?.profile?.team_name ?? "");
  const [teamNameSaved, setTeamNameSaved] = useState(false);
  const [teamNameError, setTeamNameError] = useState("");

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSentMsg, setInviteSentMsg] = useState("");
  const [inviteError, setInviteError] = useState("");

  const members = data?.members ?? [];
  const invites = data?.invites ?? [];
  const memberLimit = PLAN_MAX_MEMBERS[plan] ?? "?";

  async function handleSaveTeamName() {
    setTeamNameSaved(false);
    setTeamNameError("");
    try {
      await updateTeamName.mutateAsync(teamName);
      const newProfile = { ...currentUser.profile, team_name: teamName };
      setUser({ ...currentUser, profile: newProfile });
      localStorage.setItem("fungistics-profile", JSON.stringify(newProfile));
      setTeamNameSaved(true);
    } catch (err) {
      setTeamName(currentUser?.profile?.team_name ?? "");
      setTeamNameError(err?.message ?? "Failed to update team name.");
    }
  }

  async function handleSendInvite() {
    setInviteSentMsg("");
    setInviteError("");
    try {
      await sendInvite.mutateAsync(inviteEmail);
      setInviteSentMsg(`Invite sent to ${inviteEmail}`);
      setInviteEmail("");
    } catch (err) {
      const code = err?.body?.error;
      if (code === "user_not_found") {
        setInviteError("No Fungistics account found for that email.");
      } else if (code === "already_member") {
        setInviteError("That user is already on your team.");
      } else if (code === "already_invited") {
        setInviteError("That email already has a pending invite.");
      } else if (err?.status === 402) {
        setInviteError("Your plan's member limit has been reached.");
      } else {
        setInviteError(err?.message ?? "Something went wrong.");
      }
    }
  }

  async function handleRemoveMember(uid, email) {
    if (!window.confirm(`Remove ${email} from team?`)) return;
    try {
      await removeMember.mutateAsync(uid);
    } catch (err) {
      alert(err?.message ?? "Failed to remove member.");
    }
  }

  async function handleCancelInvite(id) {
    try {
      await cancelInvite.mutateAsync(id);
    } catch (err) {
      alert(err?.message ?? "Failed to cancel invite.");
    }
  }

  if (isLoading) return <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4"><Spinner /></div>;
  if (error) return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4 border border-red-300 dark:border-red-800">
      <p className="text-sm text-red-600 dark:text-red-400">Error loading team: {error?.message}</p>
    </div>
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4">
      {/* Team name */}
      <div className="mb-6 pb-6 border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">Team Name</h3>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={teamName}
            onChange={(e) => { setTeamName(e.target.value); setTeamNameSaved(false); setTeamNameError(""); }}
            maxLength={80}
            className="border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 flex-1 min-w-48"
          />
          <button
            onClick={handleSaveTeamName}
            disabled={updateTeamName.isPending || !teamName.trim()}
            className="text-sm bg-sky-600 dark:bg-sky-700 text-white rounded-lg px-4 py-2 hover:bg-sky-700 dark:hover:bg-sky-600 transition-colors disabled:opacity-50"
          >
            {updateTeamName.isPending ? "Saving…" : "Save"}
          </button>
        </div>
        {teamNameSaved && <p className="mt-2 text-sm text-green-600 dark:text-green-400">Saved</p>}
        {teamNameError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{teamNameError}</p>}
      </div>

      <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
        Team Members — {members.length} / {memberLimit}
      </h2>

      {/* Members list */}
      <div className="space-y-2 mb-6">
        {members.map((m) => (
          <div key={m.uid} className="flex flex-wrap items-center justify-between gap-2 py-2 px-3 rounded-lg bg-gray-50 dark:bg-gray-700">
            <div>
              <span className="text-sm text-gray-800 dark:text-gray-100">{m.email}</span>
              <span className={classNames(
                "ml-2 text-xs px-2 py-0.5 rounded-full font-medium",
                m.role === "owner"
                  ? "bg-sky-100 dark:bg-sky-950 text-sky-700 dark:text-sky-400"
                  : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300"
              )}>
                {m.role === "owner" ? "Owner" : "Member"}
              </span>
            </div>
            {m.uid !== currentUID && m.role !== "owner" && (
              <button
                onClick={() => handleRemoveMember(m.uid, m.email)}
                className="text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Pending invites */}
      {invites.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">Pending Invites</h3>
          <div className="space-y-2">
            {invites.map((inv) => (
              <div key={inv.id} className="flex flex-wrap items-center justify-between gap-2 py-2 px-3 rounded-lg bg-yellow-50 dark:bg-yellow-950">
                <div>
                  <span className="text-sm text-gray-800 dark:text-gray-100">{inv.email}</span>
                  <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                    Expires {new Date(inv.expires_at).toLocaleDateString()}
                  </span>
                </div>
                <button
                  onClick={() => handleCancelInvite(inv.id)}
                  className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite form */}
      <div>
        <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">Invite Someone</h3>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => { setInviteEmail(e.target.value); setInviteSentMsg(""); setInviteError(""); }}
            placeholder="colleague@example.com"
            className="border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 flex-1 min-w-48"
          />
          <button
            onClick={handleSendInvite}
            disabled={sendInvite.isPending || !inviteEmail.includes("@")}
            className="text-sm bg-sky-600 dark:bg-sky-700 text-white rounded-lg px-4 py-2 hover:bg-sky-700 dark:hover:bg-sky-600 transition-colors disabled:opacity-50"
          >
            {sendInvite.isPending ? "Sending…" : "Send Invite"}
          </button>
        </div>
        {inviteSentMsg && <p className="mt-2 text-sm text-green-600 dark:text-green-400">{inviteSentMsg}</p>}
        {inviteError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{inviteError}</p>}
      </div>
    </div>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("general");

  const { teamRole, plan, planLoading } = usePlan();
  const isOwner = teamRole === "owner";

  const tabs = [
    { id: "general", label: "General" },
    ...(isOwner ? [{ id: "team", label: "Team" }] : []),
  ];

  const getSettings = async () => {
    try {
      const data = await apiFetch("settings");
      setSettings(data);
    } catch (error) {
      Sentry.captureException(error);
      setError(error.message);
      console.error("API fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSettings();
  }, []);

  if (loading) {
    return (
      <Layout title="Settings">
        <div className="container w-full rounded-xl bg-white dark:bg-gray-800 shadow p-4 h-screen">
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Settings">
        <div className="container w-full rounded-xl bg-white dark:bg-gray-800 shadow p-4 min-h-40">
          <div className="dark:text-gray-100">Error loading settings: {error}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Settings">
      {/* Tab nav */}
      {!planLoading && (
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={classNames(
                  activeTab === tab.id
                    ? "border-sky-600 dark:border-sky-400 text-sky-700 dark:text-sky-400"
                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600",
                  "whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium"
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {activeTab === "general" && settings && (
        <>
          <TimezoneSettings />
          {settings.thresholds && <SettingsThresholds thresholds={settings.thresholds} />}
        </>
      )}

      {activeTab === "team" && isOwner && (
        <TeamSettings plan={plan} />
      )}
    </Layout>
  );
}

